import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect, cloneElement, ReactElement } from "react";
import { MarketHeader } from "../../MarketHeader/MarketHeader";
import { MarketFooter } from "../../MarketFooter/MarketFooter";
import { Icon } from "components/src/components/Icon/Icon";
import { Nav } from "components/src/components/Nav/Nav";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { useCssFilter } from "shared/src/hooks/useCssFilter";
import { handleShowMoreClick } from "../../../js/handleShowMoreClick";
import { prepareFilters } from "../../../js/prepareFilters";
import type { PillNavItemObjType } from "components/src/components/Nav/types";
import type { MarketHeaderType } from "../../MarketHeader/types";

type MarketElement = ReactElement<{ 
  selections?: unknown[]; 
  marketHeader?: MarketHeaderType & { filters?: unknown[]; showLimit?: number | "all" };
  filterBy?: string;
  competitor?: { team?: string };
  dataFilterGroup?: string;
  "data-filter-group"?: string;
}>;

type MarketGroup = {
  id: string;
  marketHeader: (MarketHeaderType & { filters?: unknown[]; showLimit?: number | "all" }) | null;
  markets: { market: MarketElement; index: number }[];
};

type MarketGroupWithFiltersProps = {
  group: MarketGroup;
  idPrefix: string;
  sport?: string;
  competitionType?: string;
};

type GroupedHorizontalProps = {
  mappedMarkets: MarketElement[];
  marketHeader?: MarketHeaderType;
  sport?: string;
  competitionType?: string;
};

// Groups markets by their marketHeader - markets with filters start a new group
const groupMarketsByHeader = (mappedMarkets: MarketElement[]): MarketGroup[] => {
  const groups: MarketGroup[] = [];
  let currentGroup: MarketGroup | null = null;

  mappedMarkets.forEach((market, i) => {
    const hasFilters = (market?.props?.marketHeader?.filters?.length ?? 0) > 0;
    
    if (hasFilters) {
      currentGroup = {
        id: `group-${i}`,
        marketHeader: market?.props?.marketHeader ?? null,
        markets: [{ market, index: i }],
      };
      groups.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.markets.push({ market, index: i });
    } else {
      currentGroup = {
        id: `group-${i}`,
        marketHeader: null,
        markets: [{ market, index: i }],
      };
      groups.push(currentGroup);
    }
  });

  return groups;
};

const MarketGroupWithFilters = ({ group, idPrefix, sport, competitionType }: MarketGroupWithFiltersProps) => {
  const groupId = useIdRef({ str: group.id || "cui__gh-group-" });
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [_activeFilter, setActiveFilter] = useState("");
  const [hasBeenToggled, setHasBeenToggled] = useState(false);
  const [finishedRenderSetup, setFinishedRenderSetup] = useState({
    firstTime: true,
    value: false,
  });

  const rawFilters = group.marketHeader?.filters;
  const filters = useMemo(() => 
    prepareFilters(rawFilters, sport, "rowgrouped", undefined, undefined, undefined, competitionType),
    [rawFilters, sport, competitionType]
  ) as PillNavItemObjType[] | null;
  const hasFilters = filters && filters.length > 0;
  const rawShowLimit = group.marketHeader?.showLimit;
  const initialShowLimit = rawShowLimit === "all" || rawShowLimit === undefined ? Infinity : rawShowLimit;
  const { current: _initialShowLimit } = useRef(initialShowLimit);
  const [_showLimit, setShowLimit] = useState<number | "all">(rawShowLimit ?? "all");

  // Trigger filter processing after ref is available
  useEffect(() => {
    if (ref && _activeFilter === "" && hasFilters) {
      setActiveFilter("all");
      setHasBeenToggled(true);
    }
  }, [ref, _activeFilter, hasFilters]);

  const onActiveItem = useCallback(
    (item: string | number) => {
      if (hasFilters) {
        const active = filters?.find(f => f.id === item);
        const isAll = String(active?.value)?.toLowerCase() === "all";
        const filterValue = isAll ? "all" : `${groupId.current}-${active?.value}`;
        setActiveFilter(filterValue || "all");
        if (!hasBeenToggled) setHasBeenToggled(true);
      }
    },
    [hasFilters, filters, hasBeenToggled, groupId]
  );

  const handleNavClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const totalSelections = useMemo(() => 
    group.markets.reduce((sum, { market }) => sum + (market?.props?.selections?.length || 0), 0),
    [group.markets]
  );
  // const canFilter = hasFilters && (typeof _initialShowLimit === "number" && totalSelections > _initialShowLimit);

  const [filteredElements] = useCssFilter(
    ref,
    _activeFilter,
    _showLimit,
    null,
    hasFilters,
    hasBeenToggled,
    "standard",
    finishedRenderSetup,
    setFinishedRenderSetup,
    !hasFilters
  );

  // Sync parent wrapper visibility with first child
  useLayoutEffect(() => {
    if (!ref || !hasFilters) return;
    const wrappers = ref.querySelectorAll<HTMLElement>("[data-filter-group-parent]");
    wrappers.forEach((wrapper) => {
      const firstChild = wrapper.querySelector<HTMLElement>("[data-filter-group]");
      wrapper.style.display = firstChild?.style.display === "none" ? "none" : "";
    });
  }, [ref, _activeFilter, _showLimit, hasFilters]);
  
  const showMoreTotal = useMemo(() => {
    if (_showLimit === "all") return 0;
    const limit = _showLimit as number;
    if (_activeFilter === "all" || _activeFilter === "" || !hasFilters) {
      return totalSelections - limit;
    } else {
      return (filteredElements?.length || 0) - limit;
    }
  }, [totalSelections, _showLimit, _activeFilter, filteredElements, hasFilters]);

  const _onShowMoreClick = useCallback(() => {
    handleShowMoreClick({
      setShowLimit,
      _showLimit,
      showLimit: _initialShowLimit,
      onShowMoreClick: () => {},
    });
  }, [_showLimit, _initialShowLimit]);

  const canShowLess = _showLimit === "all";
  const showFooter = hasFilters && totalSelections > _initialShowLimit;

  return (
    <div ref={setRef} className="cui__grouped-horizontal-filter-group cui-flex cui-flex-col cui-gap-2xs">
      {group.marketHeader && (
        <MarketHeader
          {...group.marketHeader}
          noBorder
          preserveGridCells={false}
          useSideSGPLabel={group.marketHeader?.useSideSGPLabel}
          noLabels
        />
      )}
      {hasFilters && (
        <div className="cui-mb-sm">
          <Nav
            className="cui-mt-xs"
            size="xs"
            items={filters}
            cssMode={true}
            showControls="none"
            overlay={true}
            onActiveItem={onActiveItem}
            skipLink={null}
            tabbable={false}
            variant="pill"
            simple
            forceMaxWidth={false}
            onClick={handleNavClick}
          />
        </div>
      )}
      {group.markets.map(({ market, index }) => {
        const selectionsLength = market?.props?.selections?.length ?? 0;
        const colLength = selectionsLength > 3 ? 3 : selectionsLength;
        const isFirstInGroup = index === group.markets[0].index;
        const hasMarketHeader = !!market?.props?.marketHeader && 
          (!isFirstInGroup || !!market?.props?.marketHeader?.title);
        const teamName = market?.props?.filterBy || market?.props?.competitor?.team || "all";
        const uniqueFilterGroup = hasFilters ? `${groupId.current}-${teamName}` : undefined;
        // Use the row-based filter group from marketMapper for parent-level show/hide (only when no child filters)
        const parentFilterGroup = hasFilters ? undefined : (market?.props?.dataFilterGroup || market?.props?.["data-filter-group"]);

        // Clone market with dataFilterGroup for child-level filtering (only if hasFilters)
        const marketWithFilterGroup = hasFilters 
          ? cloneElement(market, { dataFilterGroup: uniqueFilterGroup })
          : market;

        return (
          <div 
            key={`${idPrefix}-${index}`}
            {...(parentFilterGroup ? { "data-filter-group-row": parentFilterGroup } : {})}
            {...(uniqueFilterGroup ? { "data-filter-group-parent": uniqueFilterGroup } : {})}
            className="cui-mb-2px"
          >
            {hasMarketHeader && (
              <MarketHeader
                {...market?.props?.marketHeader}
                noBorder
                preserveGridCells={false}
                useSideSGPLabel={market?.props?.marketHeader?.useSideSGPLabel}
                noLabels
              />
            )}
            <div
              className="cui__market-template-grouped-horizontal--group cui-grid cui-w-full cui-gap-2xs"
              style={{ gridTemplateColumns: `repeat(${colLength}, 1fr)` }}
            >
              {marketWithFilterGroup}
            </div>
          </div>
        );
      })}
      {showFooter && (
        <MarketFooter
          showLimit={_showLimit}
          showMore={_showLimit !== "all" && showMoreTotal > 0}
          showMoreTotal={showMoreTotal}
          canShowLess={canShowLess}
          className="cui-mt-1"
          onShowMoreClick={_onShowMoreClick}
        />
      )}
    </div>
  );
};

export const GroupedHorizontal = ({ mappedMarkets, marketHeader, sport, competitionType }: GroupedHorizontalProps) => {
  const _id = useIdRef({ str: "cui__mt-rg-row-" });
  const firstSelectionsLength = mappedMarkets?.at(0)?.props?.selections?.length ?? 0;
  const masterColLength = firstSelectionsLength > 3 ? 3 : firstSelectionsLength;

  const groups = useMemo(() => groupMarketsByHeader(mappedMarkets), [mappedMarkets]);

  return (
    <div className="cui-space-y-sm">
      <div
        className="cui__market-template-grouped-horizontal--group cui-grid cui-w-full cui-gap-2xs -cui-mb-1"
        style={{ gridTemplateColumns: `repeat(${masterColLength}, 1fr)` }}
      >
        {marketHeader?.labels?.map((label, i) => {
          const labelIsObj = typeof label === "object";
          if (!label) return null;
          return (
            <div
              className="cui-text-fg-moderate heading-xs cui-text-center"
              key={`${_id?.current}-label-${i}`}
            >
              {labelIsObj && "text" in label ? label?.text : typeof label === "string" ? label : null}
              {labelIsObj && "hasSGP" in label && !!label?.hasSGP ? (
                <Icon name="ui_sgp" className="cui-ml-xs" size="sm" color="fg-primary-strong" />
              ) : null}
            </div>
          );
        })}
      </div>

      {groups.map((group) => (
        <MarketGroupWithFilters
          key={group?.id}
          group={group}
          idPrefix={_id?.current || ''}
          sport={sport}
          competitionType={competitionType}
        />
      ))}
    </div>
  );
};
